import React from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  Email,
  FetchEmailDetailsInPpcQuery,
  FetchEmailDetailsInPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AppPage, EmailDetails, ErrorComponents, useFeatureToggle, useRecentlyViewed } from '@css-xp/base';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { fetchEmailDetailsInPPC } from '../queries/fetch-email-details';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  routePaths,
  RecentlyViewedEntityEnum,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { failureMessage } from '@css-xp/ppc-common/src/state-props';
import { getEntityName } from '@css-xp/ppc-utils';
import { EmailDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import './email-page.scss';

const ATTACHMENT_ROUTE = '/gateway/api/css-xp-ppc/css-xp-ans/api/1/emails/attachments';
export const EmailPage = () => {
  const { emailId, txaId } = useParams<{ emailId: string; txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isEmailsImprovementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EMAILS_IMPROVEMENT);

  useTitle(PPC_PAGE_TITLE.EMAIL_DETAILS);
  const { updateRecentlyViewed } = useRecentlyViewed();
  const { loading, error, data } = useQuery<FetchEmailDetailsInPpcQuery, FetchEmailDetailsInPpcQueryVariables>(
    fetchEmailDetailsInPPC,
    {
      variables: {
        email: emailId,
      },
      displayName: 'EmailDetailsInPPCQuery',
    }
  );

  React.useEffect(() => {
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.EMAIL, txaId),
      metadata: {
        id: emailId,
        emailSubject: data?.fetchEmailDetails?.subject,
      },
    });
  }, [data]);

  if (loading) {
    if (isDesignIteration1Enabled) {
      return <EmailDetailsPageLoader />;
    }
    return <LoadingIndicator />;
  }
  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }

  if (!data?.fetchEmailDetails?.id) {
    const NotFound = withErrorWrapper(ErrorComponents.NotFound);
    return <NotFound />;
  }

  const emailDetails = data.fetchEmailDetails as Email;

  const renderHeading = () => {
    return <h1>Email - {emailDetails?.subject ?? ''}</h1>;
  };

  function getHeading(): React.ReactNode {
    return <div className={'email-page-heading'}>{renderHeading()}</div>;
  }

  const renderAppPage = () => {
    return (
      <AppPage data-testid="email-pane-preview" className="email-pane-preview-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_EMAIL_DETAIL_SCREEN,
            attributes: {
              id: emailId,
            },
          }}
        />
        <EmailDetails
          emailData={emailDetails}
          attachmentRoute={ATTACHMENT_ROUTE}
          errorMessage={failureMessage}
          version={isDesignIteration1Enabled ? 2 : undefined}
          isEmailsImprovementEnabled={isEmailsImprovementEnabled}
        />
      </AppPage>
    );
  };

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Billing account',
        href: `${routePaths.details}#emails`,
      },
      {
        title: 'Email details',
        href: routePaths.emailDetails,
      },
    ];

    return (
      <PageBackgroundV2 heading="Email details" breadcrumbsProps={{ items: breadcrumbsItems }}>
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={getHeading()}
      // TODO: Replace with email detail background when ready.
      imageKey={Background.INVOICE_DETAILS_BACKGROUND}
      className={'page-background'}
      isBackButtonEnabled={true}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
